import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Info } from "lucide-react";
import useSeo from "../lib/useSeo";

const WEEKS = [
  {
    label: "Week 1",
    title: "Listening, not pitching",
    body: "We start with a long, honest conversation. What is true about the business today, what has been tried, what was set aside, and what the founder is privately worried about. We leave with a working hypothesis, not a plan.",
  },
  {
    label: "Week 2",
    title: "Positioning, said out loud",
    body: "We test how the business sounds when described in plain language. Where it lands, where it loses the listener, and which phrases the founder keeps reaching for. The positioning brief is written here, then sharpened in week three.",
  },
  {
    label: "Week 3",
    title: "Customer profile, in their own words",
    body: "We narrow to the customer the business serves best, not the broadest reasonable audience. We write their world, their words, and the specific moment they decide to act.",
  },
  {
    label: "Week 4",
    title: "Offer architecture",
    body: "We map the offer ladder. What is sold, in what order, and which offers should be retired. The aim is one fewer offer, not one more.",
  },
  {
    label: "Week 5",
    title: "Messaging spine",
    body: "We build the spine the rest of the business can repeat. Headlines, opening lines, objections, and the way to say no without softening it.",
  },
  {
    label: "Week 6",
    title: "Handoff, not dependency",
    body: "We end with the founder using the work in the room. Adjustments, signoffs, and a written handover. The studio steps back. Momentum advisory is offered, never assumed.",
  },
];

const BEFORE = [
  "Three offers competing for the same buyer",
  "A team paraphrasing the founder differently each week",
  "Strong work, hidden behind unclear language",
  "Marketing rewriting what was rewritten last quarter",
];

const AFTER = [
  "One primary offer, two clearly placed companions",
  "A messaging spine the team can quote, not improvise",
  "Positioning the founder is willing to defend",
  "Decisions made faster, with less second-guessing",
];

export default function CaseStudyIllustrative() {
  useSeo({
    title: "How a Clarity Sprint runs · BuildOnce Studio",
    description:
      "How a six-week Clarity Sprint unfolds, week by week, from the first honest conversation to handover. BuildOnce Studio.",
  });
  return (
    <article data-testid="case-study-page">
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-20 lg:pt-24 pb-16">
          <Link
            to="/services"
            data-testid="case-study-back"
            className="inline-flex items-center gap-2 text-bos-primary bos-link-underline text-[14px]"
          >
            <ArrowLeft size={14} /> Back to services
          </Link>

          <div
            data-testid="case-study-eyebrow"
            className="mt-10 inline-flex items-center gap-2 text-bos-mid"
          >
            <Info size={14} />
            <span className="bos-eyebrow">Case study</span>
          </div>

          <h1
            className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-4xl"
            data-testid="case-study-title"
          >
            A Clarity Sprint <span className="text-bos-primary">in six weeks.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]">
            This is how the work unfolds, from the first honest conversation to
            the handover. The method is the same in every engagement.
          </p>
        </div>
      </section>

      <section className="border-b border-bos-ice" data-testid="case-study-before-after">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-2 gap-10">
          <div className="bg-bos-white border border-bos-ice rounded-sm p-8">
            <p className="bos-eyebrow">Before</p>
            <h2 className="mt-4 text-2xl lg:text-[28px] font-medium text-bos-navy">
              The picture, week zero.
            </h2>
            <ul className="mt-6 space-y-3" data-testid="case-study-before-list">
              {BEFORE.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-bos-charcoal/85 leading-[1.7]">
                  <span className="mt-2.5 h-1 w-1 rounded-full bg-bos-mid shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-bos-navy text-bos-white rounded-sm p-8">
            <p className="text-[12px] tracking-[0.18em] uppercase text-bos-light">After</p>
            <h2 className="mt-4 text-2xl lg:text-[28px] font-medium text-bos-white">
              The picture, week seven.
            </h2>
            <ul className="mt-6 space-y-3" data-testid="case-study-after-list">
              {AFTER.map((a) => (
                <li key={a} className="flex items-start gap-3 text-[15px] text-bos-light/95 leading-[1.7]">
                  <span className="mt-2.5 h-1 w-1 rounded-full bg-bos-light shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-b border-bos-ice" data-testid="case-study-weeks">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1fr_2fr] gap-16">
            <div>
              <p className="bos-eyebrow">The six weeks</p>
              <h2 className="mt-4 text-3xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.15]">
                Slow first, fast later.
              </h2>
              <p className="mt-6 text-[15px] text-bos-charcoal/80 leading-[1.7]">
                The cadence is deliberate. The early weeks feel slower than
                most engagements. The later weeks feel faster than most
                because the thinking has already been done.
              </p>
            </div>
            <div className="grid gap-10">
              {WEEKS.map((w, i) => (
                <div
                  key={w.label}
                  data-testid={`case-study-week-${i}`}
                  className="grid grid-cols-[auto_1fr] gap-6"
                >
                  <div className="text-[12px] tracking-[0.16em] uppercase text-bos-mid pt-1">
                    {w.label}
                  </div>
                  <div className="border-l border-bos-ice pl-6">
                    <h3 className="text-[20px] font-medium text-bos-navy">
                      {w.title}
                    </h3>
                    <p className="mt-3 text-[14px] text-bos-charcoal/85 leading-[1.75]">
                      {w.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-bos-ice bg-bos-ice/40" data-testid="case-study-pullquote">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
          <p className="bos-eyebrow">From a real engagement</p>
          <blockquote className="mt-6 text-2xl lg:text-[40px] font-medium text-bos-navy tracking-tight leading-[1.2] max-w-4xl">
            "What shifted wasn't the thinking, it was finally being able to
            say it clearly. The team has shared language now. The business
            feels easier to carry."
          </blockquote>
          <p className="mt-8 text-[13px] text-bos-charcoal/60 tracking-[0.04em]">
            From a real engagement. Name withheld.
          </p>
        </div>
      </section>

      <section className="bg-bos-navy" data-testid="case-study-cta">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-[2fr_1fr] gap-10 items-end">
          <div>
            <p className="text-[12px] tracking-[0.18em] uppercase text-bos-light">If this rhythm fits</p>
            <h2 className="mt-4 text-2xl lg:text-[40px] font-medium text-bos-white tracking-tight leading-[1.15] max-w-2xl">
              A short call is the right place to find out whether a sprint
              suits the season you are in.
            </h2>
          </div>
          <Link
            to="/booking"
            data-testid="case-study-book-cta"
            className="inline-flex items-center gap-2 bg-bos-white text-bos-navy px-7 py-3.5 rounded-full hover:bg-bos-ice transition-colors self-start"
          >
            Book a discovery call
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="border-t border-bos-ice bg-bos-white" data-testid="case-study-footnote">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-10">
          <p className="text-[12px] text-bos-charcoal/55 leading-[1.6] max-w-3xl">
            A representative sprint for a founder-led business with too many
            offers and inconsistent messaging.
          </p>
        </div>
      </section>
    </article>
  );
}
